import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import SEO from '../components/SEO/SEO';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  // Blog posts data
  const blogPosts = [
    {
      id: 7,
      title: 'From Paper Chaos to Digital Clarity: Why Your Manual Systems Are Bleeding Money',
      excerpt: 'Your organization is leaking money—quietly. Paper files, spreadsheets, and WhatsApp approvals are costing you more than you realize.',
      category: 'Digital Transformation',
      date: 'Jan 20, 2026',
      readTime: '8 min read',
      image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&auto=format&fit=crop',
      author: 'FIGO Team',
      slug: 'from-paper-chaos-to-digital-clarity',
      content: `
        <h2>Your Organization Is Leaking Money—Quietly</h2>
        <p>Right now, somewhere in your organization, critical business data is trapped in a filing cabinet. An important approval is stuck in someone's WhatsApp chat. A spreadsheet with conflicting numbers is being emailed back and forth. And nobody knows which version is correct.</p>

        <p>This isn't just inconvenience. <strong>It's a silent financial drain that's costing you far more than you realize.</strong></p>

        <h3>The Hidden Cost of "How We've Always Done It"</h3>
        <p>Manual systems don't fail with a loud crash. They fail quietly, expensively, and continuously. Here's what paper-based chaos is really costing your organization:</p>

        <h4>1. Lost Data and Costly Errors</h4>
        <p>When information lives in paper files, spreadsheets, and scattered WhatsApp messages:</p>
        <ul>
          <li>Critical documents go missing or get misfiled</li>
          <li>Data entry errors compound across multiple systems</li>
          <li>Decisions are made on outdated or incomplete information</li>
          <li>Customer records become inconsistent and unreliable</li>
        </ul>
        <p><strong>The cost:</strong> One Nigerian bank discovered they were losing ₦50 million annually to errors that originated from manual data entry across disconnected systems.</p>

        <h4>2. Delayed Reporting That Blinds Leadership</h4>
        <p>When your month-end reports take two weeks to compile:</p>
        <ul>
          <li>You're making decisions based on stale data</li>
          <li>Problems that could have been caught early become crises</li>
          <li>Opportunities pass while you're still gathering information</li>
          <li>Your competitors who have real-time dashboards are moving faster</li>
        </ul>
        <p><strong>The cost:</strong> Delayed visibility means delayed action. Every day without accurate data is a day of flying blind.</p>

        <h4>3. Audit Nightmares and Compliance Risks</h4>
        <p>Regulators and auditors don't accept "I think it's in that filing cabinet" as an answer:</p>
        <ul>
          <li>Reconstructing audit trails from paper records is expensive and time-consuming</li>
          <li>Missing documentation leads to compliance penalties</li>
          <li>NDPR and other data protection requirements are nearly impossible to meet manually</li>
          <li>Each audit becomes a scramble that disrupts normal operations</li>
        </ul>
        <p><strong>The cost:</strong> Compliance failures can result in fines, reputational damage, and in some industries, loss of operating licenses.</p>

        <h4>4. Staff Burnout and Talent Drain</h4>
        <p>Your best employees didn't join your organization to chase paper trails:</p>
        <ul>
          <li>High performers get frustrated spending hours on manual data entry</li>
          <li>Talented staff leave for organizations with modern systems</li>
          <li>Remaining employees become demoralized and disengaged</li>
          <li>Institutional knowledge walks out the door with every departure</li>
        </ul>
        <p><strong>The cost:</strong> Replacing a skilled employee costs 50-200% of their annual salary. How many have you lost to operational frustration?</p>

        <h4>5. Poor Visibility That Keeps Leaders Guessing</h4>
        <p>When leadership can't see what's really happening:</p>
        <ul>
          <li>Strategic decisions are based on gut feeling instead of data</li>
          <li>Resource allocation is a guessing game</li>
          <li>Performance problems go undetected until they become critical</li>
          <li>Growth opportunities are missed because nobody saw them coming</li>
        </ul>
        <p><strong>The cost:</strong> The organizations that will dominate the next decade are those with clear, real-time visibility into their operations.</p>

        <h3>The WhatsApp Approval Trap</h3>
        <p>Let's talk about something specific: using WhatsApp for business approvals.</p>

        <p>It seems convenient—everyone has it, it's instant, and it creates a record. But here's what's really happening:</p>
        <ul>
          <li>Approvals get lost in chat histories</li>
          <li>There's no way to track approval status across multiple requests</li>
          <li>When someone leaves, their approval history goes with them</li>
          <li>Auditors can't verify decisions made in personal chat apps</li>
          <li>Sensitive business information is sitting on personal devices</li>
        </ul>

        <p><strong>WhatsApp is great for quick communication. It's terrible for business processes that need accountability, tracking, and audit trails.</strong></p>

        <h3>The Spreadsheet Illusion</h3>
        <p>Spreadsheets are powerful tools—for analysis. They were never designed to be databases, workflow systems, or sources of truth for your business.</p>

        <p>When you run critical operations on spreadsheets:</p>
        <ul>
          <li>Multiple versions exist, and nobody knows which is correct</li>
          <li>Formula errors propagate silently</li>
          <li>Data validation is minimal or non-existent</li>
          <li>Collaboration becomes a nightmare of "Final_v2_FINAL_revised.xlsx"</li>
          <li>Security is virtually impossible to enforce</li>
        </ul>

        <h3>What Digital Clarity Actually Looks Like</h3>
        <p>Imagine walking into work on Monday morning and:</p>
        <ul>
          <li>Your dashboard shows exactly how last week performed—automatically compiled</li>
          <li>Pending approvals are in a queue, not scattered across chat apps</li>
          <li>Every document is findable in seconds, not hours</li>
          <li>Your team is focused on strategic work, not data entry</li>
          <li>Audit preparation takes hours, not weeks</li>
          <li>You can see problems developing before they become crises</li>
        </ul>

        <p><strong>This isn't a fantasy. This is what digital operations look like.</strong></p>

        <h3>The Path From Chaos to Clarity</h3>
        <p>Digital transformation doesn't mean ripping everything out and starting over. It means systematically replacing chaos with clarity:</p>

        <p><strong>Step 1: Assessment</strong></p>
        <p>Understand where you are. Map your current processes. Identify the biggest pain points and the highest-impact opportunities.</p>

        <p><strong>Step 2: Prioritization</strong></p>
        <p>You can't fix everything at once. Start with the processes that are costing you the most or creating the biggest risks.</p>

        <p><strong>Step 3: Implementation</strong></p>
        <p>Deploy solutions that solve real problems. Not technology for technology's sake, but tools that make your people more effective.</p>

        <p><strong>Step 4: Integration</strong></p>
        <p>Connect your systems so data flows automatically. Eliminate the manual bridges that create errors and delays.</p>

        <p><strong>Step 5: Optimization</strong></p>
        <p>Continuously improve. Use the visibility you now have to identify and eliminate inefficiencies.</p>

        <h3>The Real Question</h3>
        <p>The question isn't whether you can afford to digitize your operations.</p>

        <p><strong>The question is: how much longer can you afford not to?</strong></p>

        <p>Every month you wait:</p>
        <ul>
          <li>More money leaks through operational inefficiency</li>
          <li>More talented employees get frustrated and leave</li>
          <li>More competitors pull ahead with better systems</li>
          <li>More risks accumulate in your compliance posture</li>
        </ul>

        <h3>Take the First Step Today</h3>
        <p>At FIGO Digital Services, we specialize in helping organizations across Nigeria transform from paper chaos to digital clarity. We understand the unique challenges of operating in this environment, and we know how to navigate them.</p>

        <p>Our Digital Operations Assessment gives you a clear picture of where you are, where you need to be, and the most effective path to get there.</p>

        <p><strong>Stop the silent bleed. Start the transformation.</strong></p>
      `,
      primaryCTA: {
        text: 'Request a Digital Operations Assessment',
        link: '/contact?service=transform'
      },
      secondaryCTA: {
        text: 'See How It Works',
        link: '/services/transform'
      }
    },
    {
      id: 1,
      title: 'The Future of AI in Business Automation',
      excerpt: 'Discover how artificial intelligence is transforming the way businesses operate and compete in the digital age.',
      category: 'AI & Automation',
      date: 'Jan 5, 2026',
      readTime: '5 min read',
      image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&auto=format&fit=crop',
      author: 'FIGO Team',
      slug: 'future-of-ai-business-automation',
      content: `
        <h2>The AI Revolution in Business</h2>
        <p>Artificial Intelligence is no longer a futuristic concept—it's here, and it's transforming how businesses operate across the globe. In Nigeria and across Africa, we're witnessing a surge in AI adoption that's reshaping industries and creating new opportunities.</p>

        <h3>Why AI Matters for Your Business</h3>
        <p>AI automation can help businesses:</p>
        <ul>
          <li>Reduce operational costs by up to 30%</li>
          <li>Improve customer service with 24/7 availability</li>
          <li>Make data-driven decisions faster</li>
          <li>Scale operations without proportional cost increases</li>
        </ul>

        <h3>Real-World Applications</h3>
        <p>From chatbots handling customer inquiries to predictive analytics forecasting market trends, AI is making businesses smarter and more efficient. Nigerian companies are particularly well-positioned to leverage AI for competitive advantage in their markets.</p>

        <h3>Getting Started with AI</h3>
        <p>The key is to start small. Identify one process that could benefit from automation, test it, measure results, and scale from there. At FIGO Digital Services, we help businesses navigate this journey with custom AI solutions tailored to their specific needs.</p>

        <h3>The Future is Now</h3>
        <p>The businesses that thrive in the coming years will be those that embrace AI today. Don't wait for your competitors to gain the advantage—start your AI transformation journey now.</p>
      `
    },
    {
      id: 2,
      title: 'Building Scalable Web Applications in 2026',
      excerpt: 'Learn the best practices and modern technologies for creating web applications that scale with your business growth.',
      category: 'Software Development',
      date: 'Dec 28, 2025',
      readTime: '7 min read',
      image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&auto=format&fit=crop',
      author: 'FIGO Team',
      slug: 'building-scalable-web-applications',
      content: `
        <h2>Scalability: The Foundation of Modern Web Apps</h2>
        <p>Building a web application is one thing. Building one that can grow with your business is entirely different. Scalability isn't just about handling more users—it's about maintaining performance, reliability, and user experience as your business grows.</p>

        <h3>Key Principles of Scalable Architecture</h3>
        <p>Modern scalable applications are built on several core principles:</p>
        <ul>
          <li><strong>Microservices Architecture:</strong> Breaking down your application into independent, deployable services</li>
          <li><strong>Cloud-Native Design:</strong> Leveraging cloud infrastructure for flexibility and scalability</li>
          <li><strong>Database Optimization:</strong> Choosing the right database and optimizing queries</li>
          <li><strong>Caching Strategy:</strong> Implementing smart caching to reduce server load</li>
        </ul>

        <h3>Technology Stack Matters</h3>
        <p>Choosing the right technology stack is crucial. In 2026, popular choices include:</p>
        <ul>
          <li>React or Next.js for frontend</li>
          <li>Node.js, Python, or Go for backend</li>
          <li>PostgreSQL or MongoDB for databases</li>
          <li>Redis for caching</li>
          <li>AWS, Google Cloud, or Azure for hosting</li>
        </ul>

        <h3>Performance Monitoring</h3>
        <p>You can't improve what you don't measure. Implement comprehensive monitoring from day one to track performance metrics, identify bottlenecks, and optimize accordingly.</p>

        <h3>Planning for Growth</h3>
        <p>At FIGO Digital Services, we build applications with scalability in mind from the start. This approach saves significant time and money compared to rebuilding later when growth demands it.</p>
      `
    },
    {
      id: 3,
      title: 'Digital Transformation: A Complete Guide for Nigerian Businesses',
      excerpt: 'Everything you need to know about successfully implementing digital transformation in your organization.',
      category: 'Digital Transformation',
      date: 'Dec 20, 2025',
      readTime: '10 min read',
      image: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&auto=format&fit=crop',
      author: 'FIGO Team',
      slug: 'digital-transformation-guide-nigerian-businesses',
      content: `
        <h2>What is Digital Transformation?</h2>
        <p>Digital transformation is more than just adopting new technology. It's a fundamental shift in how your business operates, delivers value to customers, and competes in the market. For Nigerian businesses, it's becoming not just an advantage, but a necessity.</p>

        <h3>Why Nigerian Businesses Need Digital Transformation</h3>
        <p>The Nigerian market is evolving rapidly. Customers expect digital experiences, competitors are adopting new technologies, and efficiency gains from digitalization are too significant to ignore.</p>

        <h3>The Digital Transformation Roadmap</h3>
        <p><strong>1. Assessment Phase</strong></p>
        <p>Understand your current state. Where are the manual processes? What data aren't you capturing? Where are customers frustrated?</p>

        <p><strong>2. Strategy Development</strong></p>
        <p>Define clear objectives. What do you want to achieve? Better customer experience? Operational efficiency? New revenue streams?</p>

        <p><strong>3. Technology Selection</strong></p>
        <p>Choose the right tools for your needs:</p>
        <ul>
          <li>CRM systems for customer management</li>
          <li>ERP systems for operations</li>
          <li>E-commerce platforms for online sales</li>
          <li>Analytics tools for data-driven decisions</li>
        </ul>

        <p><strong>4. Implementation</strong></p>
        <p>Start with pilot projects. Test, learn, and scale what works.</p>

        <p><strong>5. Change Management</strong></p>
        <p>Technology is only half the battle. Getting your team on board is crucial.</p>

        <h3>Common Pitfalls to Avoid</h3>
        <ul>
          <li>Trying to transform everything at once</li>
          <li>Choosing technology before understanding needs</li>
          <li>Ignoring change management</li>
          <li>Not measuring results</li>
        </ul>

        <h3>What Makes Digital Transformation Succeed</h3>
        <p>Successful digital transformation across Nigerian businesses, from small startups to established enterprises, shares common elements: A clear strategy, the right technology partner, and commitment to change.</p>

        <h3>Your Next Steps</h3>
        <p>Ready to begin your digital transformation journey? Start by assessing where you are and where you want to be. FIGO Digital Services is ready to help you navigate every step of the process.</p>
      `
    },
    {
      id: 4,
      title: 'Mobile-First Development: Why It Matters in Africa',
      excerpt: 'Understanding the importance of mobile-first approach in developing markets and how to implement it effectively.',
      category: 'Mobile Development',
      date: 'Dec 15, 2025',
      readTime: '6 min read',
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1200&auto=format&fit=crop',
      author: 'FIGO Team',
      slug: 'mobile-first-development-africa',
      content: `
        <h2>The Mobile Revolution in Africa</h2>
        <p>In Africa, mobile isn't just important—it's primary. With smartphone penetration growing rapidly and desktop usage relatively low, building mobile-first is not just best practice, it's essential.</p>

        <h3>The African Mobile Landscape</h3>
        <p>Key statistics that highlight why mobile-first matters:</p>
        <ul>
          <li>Over 70% of internet users access the web primarily via mobile</li>
          <li>Mobile payment adoption is among the highest globally</li>
          <li>Data costs make lightweight, efficient apps crucial</li>
        </ul>

        <h3>Mobile-First Design Principles</h3>
        <p>Building for mobile first means:</p>
        <ul>
          <li><strong>Prioritizing Essential Content:</strong> Limited screen space means focusing on what matters most</li>
          <li><strong>Touch-Friendly Interfaces:</strong> Large buttons, adequate spacing, intuitive gestures</li>
          <li><strong>Performance Optimization:</strong> Fast loading times even on slow connections</li>
          <li><strong>Offline Functionality:</strong> Working even with intermittent connectivity</li>
        </ul>

        <h3>Technical Considerations</h3>
        <p>Progressive Web Apps (PWAs) are particularly relevant for African markets. They combine the best of web and mobile apps, work offline, and don't require app store downloads.</p>

        <h3>Real-World Impact</h3>
        <p>We've seen businesses increase engagement by 200%+ simply by optimizing their mobile experience. In markets where mobile is primary, this isn't optional—it's critical for success.</p>
      `
    },
    {
      id: 5,
      title: 'Cybersecurity Best Practices for Small Businesses',
      excerpt: 'Essential security measures every small business should implement to protect their digital assets.',
      category: 'Security',
      date: 'Dec 10, 2025',
      readTime: '8 min read',
      image: 'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=1200&auto=format&fit=crop',
      author: 'FIGO Team',
      slug: 'cybersecurity-best-practices',
      content: `
        <h2>Why Small Businesses Are Targets</h2>
        <p>Small businesses often think they're too small to be targeted by cybercriminals. The reality is quite different—small businesses are frequently targeted precisely because they often have weaker security measures.</p>

        <h3>Essential Security Measures</h3>
        <p><strong>1. Strong Password Policies</strong></p>
        <ul>
          <li>Require complex passwords</li>
          <li>Implement multi-factor authentication</li>
          <li>Use a password manager</li>
          <li>Regular password updates</li>
        </ul>

        <p><strong>2. Regular Software Updates</strong></p>
        <p>Outdated software is one of the most common entry points for attacks. Enable automatic updates wherever possible.</p>

        <p><strong>3. Data Backup Strategy</strong></p>
        <p>Follow the 3-2-1 rule:</p>
        <ul>
          <li>3 copies of your data</li>
          <li>2 different storage types</li>
          <li>1 off-site backup</li>
        </ul>

        <p><strong>4. Employee Training</strong></p>
        <p>Your employees are your first line of defense. Regular security awareness training is essential.</p>

        <p><strong>5. Secure Your Network</strong></p>
        <ul>
          <li>Use a firewall</li>
          <li>Encrypt Wi-Fi networks</li>
          <li>Use VPNs for remote access</li>
          <li>Segment your network</li>
        </ul>

        <h3>Common Threats to Watch For</h3>
        <ul>
          <li><strong>Phishing:</strong> Fraudulent emails trying to steal credentials</li>
          <li><strong>Ransomware:</strong> Malware that encrypts your data for ransom</li>
          <li><strong>Social Engineering:</strong> Manipulating people into giving up sensitive information</li>
        </ul>

        <h3>Incident Response Plan</h3>
        <p>Have a plan for when (not if) a security incident occurs. Know who to contact, what steps to take, and how to communicate with affected parties.</p>

        <h3>Professional Support</h3>
        <p>Consider partnering with cybersecurity professionals. At FIGO Digital Services, we offer security assessments and ongoing protection to keep your business safe.</p>
      `
    },
    {
      id: 6,
      title: 'The Rise of Fintech in Nigeria: Opportunities and Challenges',
      excerpt: 'Exploring the booming fintech ecosystem in Nigeria and what it means for entrepreneurs and developers.',
      category: 'Fintech',
      date: 'Dec 5, 2025',
      readTime: '9 min read',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&auto=format&fit=crop',
      author: 'FIGO Team',
      slug: 'fintech-nigeria-opportunities',
      content: `
        <h2>Nigeria's Fintech Boom</h2>
        <p>Nigeria has emerged as Africa's fintech leader, with innovative companies solving real problems and creating massive value. The ecosystem is vibrant, well-funded, and growing rapidly.</p>

        <h3>Key Drivers of Growth</h3>
        <ul>
          <li><strong>Large Unbanked Population:</strong> Millions of Nigerians lack access to traditional banking</li>
          <li><strong>Mobile Penetration:</strong> High smartphone adoption enables mobile financial services</li>
          <li><strong>Young, Tech-Savvy Population:</strong> Ready to adopt digital financial solutions</li>
          <li><strong>Regulatory Support:</strong> Progressive policies from the Central Bank of Nigeria</li>
        </ul>

        <h3>Major Opportunity Areas</h3>
        <p><strong>1. Digital Payments</strong></p>
        <p>From peer-to-peer transfers to merchant payments, digital payment solutions are transforming how Nigerians transact.</p>

        <p><strong>2. Digital Banking</strong></p>
        <p>Neobanks offering banking services without physical branches are gaining rapid adoption.</p>

        <p><strong>3. Lending Platforms</strong></p>
        <p>Digital lending platforms using alternative credit scoring are providing access to credit for previously underserved populations.</p>

        <p><strong>4. Investment Tech</strong></p>
        <p>Platforms democratizing access to investment opportunities, from stocks to real estate.</p>

        <p><strong>5. Insurance Tech</strong></p>
        <p>Making insurance more accessible and affordable through technology.</p>

        <h3>Challenges to Navigate</h3>
        <ul>
          <li><strong>Regulatory Compliance:</strong> Navigating complex and evolving regulations</li>
          <li><strong>Cybersecurity:</strong> Protecting sensitive financial data</li>
          <li><strong>Customer Trust:</strong> Building and maintaining user confidence</li>
          <li><strong>Infrastructure:</strong> Dealing with connectivity and power challenges</li>
        </ul>

        <h3>Technology Stack for Fintech</h3>
        <p>Building fintech solutions requires robust, secure technology:</p>
        <ul>
          <li>Secure API architecture</li>
          <li>End-to-end encryption</li>
          <li>Real-time transaction processing</li>
          <li>Comprehensive logging and monitoring</li>
          <li>Scalable cloud infrastructure</li>
        </ul>

        <h3>The Future of Nigerian Fintech</h3>
        <p>The future is bright. As the ecosystem matures, we'll see more sophisticated solutions, deeper financial inclusion, and continued innovation. For entrepreneurs and developers, the opportunities are immense.</p>

        <h3>Building Your Fintech Solution</h3>
        <p>Whether you're launching a new fintech startup or adding financial services to your existing business, FIGO Digital Services can help you build secure, scalable, compliant solutions.</p>
      `
    }
  ];

  // Find the current post
  const post = blogPosts.find(p => p.slug === slug);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // If post not found, redirect to blog page
  useEffect(() => {
    if (!post) {
      navigate('/blog');
    }
  }, [post, navigate]);

  if (!post) {
    return null;
  }

  // Get related posts (same category, excluding current post)
  const relatedPosts = blogPosts
    .filter(p => p.category === post.category && p.slug !== post.slug)
    .slice(0, 3);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-primary)',
      paddingTop: '100px'
    }}>
      <SEO
        title={`${post.title} - FIGO Digital Services Blog`}
        description={post.excerpt}
        keywords={`${post.category}, ${post.title}, tech blog Nigeria`}
        url={`https://figodigitalservices.com/blog/${post.slug}`}
      />

      {/* Hero Section */}
      <section style={{
        padding: '4rem 5%',
        background: 'var(--bg-secondary)',
        borderBottom: '1px solid #E2E8F0'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {/* Breadcrumb */}
          <motion.div
            initial={{ y: 15 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '2rem',
              fontSize: '0.9rem',
              color: 'var(--text-secondary)'
            }}
          >
            <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>Home</Link>
            <span>/</span>
            <Link to="/blog" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>Blog</Link>
            <span>/</span>
            <span style={{ color: 'var(--figo-primary)' }}>{post.title}</span>
          </motion.div>

          {/* Category Badge */}
          <motion.div
            initial={{ y: 15 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{ marginBottom: '1.5rem' }}
          >
            <span style={{
              display: 'inline-block',
              padding: '0.5rem 1rem',
              background: 'var(--gradient-1)',
              borderRadius: '50px',
              color: 'white',
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              fontWeight: '600'
            }}>
              {post.category}
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ y: 15 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{
              fontSize: 'clamp(2rem, 5vw, 3.5rem)',
              color: 'var(--text-primary)',
              marginBottom: '1.5rem',
              fontWeight: '700',
              lineHeight: '1.2'
            }}
          >
            {post.title}
          </motion.h1>

          {/* Meta Info */}
          <motion.div
            initial={{ y: 15 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '2rem',
              flexWrap: 'wrap',
              paddingBottom: '2rem',
              borderBottom: '1px solid #E2E8F0'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'var(--gradient-1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px'
              }}>
                <img
                  src="/figo-logo9.png"
                  alt="FIGO"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
              </div>
              <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{post.author}</span>
            </div>
            <span style={{ color: 'var(--text-secondary)' }}>{post.date}</span>
            <span style={{ color: 'var(--figo-primary)', fontWeight: '600' }}>{post.readTime}</span>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      <section style={{ padding: '0 5%', marginTop: '3rem' }}>
        <motion.div
          initial={{ scale: 0.97 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.6 }}
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
          }}
        >
          <img
            src={post.image}
            alt={post.title}
            style={{
              width: '100%',
              height: 'auto',
              display: 'block'
            }}
          />
        </motion.div>
      </section>

      {/* Article Content */}
      <article style={{ padding: '4rem 5%' }}>
        <motion.div
          initial={{ y: 15 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{
            maxWidth: '800px',
            margin: '0 auto',
            color: 'var(--text-primary)',
            fontSize: '1.1rem',
            lineHeight: '1.8'
          }}
        >
          <div
            dangerouslySetInnerHTML={{ __html: post.content }}
            className="blog-content"
          />

          {/* Post CTAs */}
          {(post.primaryCTA || post.secondaryCTA) && (
            <div style={{
              marginTop: '3rem',
              padding: '2.5rem',
              background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.06) 0%, rgba(30, 64, 175, 0.06) 100%)',
              borderRadius: '20px',
              border: '1px solid rgba(37, 99, 235, 0.15)',
              textAlign: 'center'
            }}>
              <h3 style={{
                color: 'var(--text-primary)',
                fontSize: '1.5rem',
                marginBottom: '1rem',
                fontWeight: '700'
              }}>
                Ready to Transform Your Operations?
              </h3>
              <p style={{
                color: 'var(--text-secondary)',
                marginBottom: '2rem',
                fontSize: '1rem'
              }}>
                Take the first step towards digital clarity today.
              </p>
              <div style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                {post.primaryCTA && (
                  <Link
                    to={post.primaryCTA.link}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '1rem 2rem',
                      background: 'var(--gradient-1)',
                      borderRadius: '50px',
                      color: 'white',
                      fontSize: '1rem',
                      fontWeight: '600',
                      textDecoration: 'none',
                      transition: 'transform 0.3s ease',
                      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)'
                    }}
                  >
                    {post.primaryCTA.text}
                  </Link>
                )}
                {post.secondaryCTA && (
                  <Link
                    to={post.secondaryCTA.link}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '1rem 2rem',
                      background: 'transparent',
                      borderRadius: '50px',
                      color: 'var(--figo-primary)',
                      fontSize: '1rem',
                      fontWeight: '600',
                      textDecoration: 'none',
                      border: '2px solid var(--figo-primary)',
                      transition: 'background 0.3s ease, color 0.3s ease'
                    }}
                  >
                    {post.secondaryCTA.text}
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Share Section */}
          <div style={{
            marginTop: '3rem',
            paddingTop: '2rem',
            borderTop: '1px solid #E2E8F0',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            flexWrap: 'wrap'
          }}>
            <p style={{
              color: 'var(--text-secondary)',
              margin: 0
            }}>
              Found this article helpful?
            </p>
            <button
              onClick={handleCopyLink}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.625rem 1.25rem',
                background: copied ? '#22c55e' : 'var(--figo-primary)',
                border: 'none',
                borderRadius: '50px',
                color: 'white',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background 0.3s ease'
              }}
            >
              {copied ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M13.707 4.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-3-3a1 1 0 011.414-1.414L7 9.586l5.293-5.293a1 1 0 011.414 0z" fill="currentColor"/>
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="5" y="5" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                    <path d="M3 11V3.5A.5.5 0 013.5 3H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  Copy Link
                </>
              )}
            </button>
          </div>
        </motion.div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section style={{
          padding: '4rem 5%',
          background: 'var(--bg-secondary)',
          borderTop: '1px solid #E2E8F0'
        }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              color: 'var(--text-primary)',
              marginBottom: '2.5rem',
              textAlign: 'center',
              fontWeight: '700'
            }}>
              Related Articles
            </h2>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '2rem'
              }}
            >
              {relatedPosts.map((relatedPost) => (
                <article
                  key={relatedPost.id}
                  style={{
                    background: '#ffffff',
                    border: '1px solid #E2E8F0',
                    borderRadius: '20px',
                    overflow: 'hidden',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <Link to={`/blog/${relatedPost.slug}`} style={{ textDecoration: 'none' }}>
                    <div style={{
                      height: '200px',
                      overflow: 'hidden'
                    }}>
                      <img
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.5s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      />
                    </div>

                    <div style={{ padding: '1.5rem' }}>
                      <span style={{
                        color: 'var(--figo-primary)',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '1.5px',
                        fontWeight: '600'
                      }}>
                        {relatedPost.category}
                      </span>

                      <h3 style={{
                        fontSize: '1.2rem',
                        color: 'var(--text-primary)',
                        margin: '0.75rem 0',
                        fontWeight: '700',
                        lineHeight: '1.4'
                      }}>
                        {relatedPost.title}
                      </h3>

                      <p style={{
                        color: 'var(--text-secondary)',
                        fontSize: '0.9rem',
                        lineHeight: '1.6'
                      }}>
                        {relatedPost.excerpt}
                      </p>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section style={{
        padding: '5rem 5%',
        background: 'var(--bg-primary)',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
            color: 'var(--text-primary)',
            marginBottom: '1.5rem',
            fontWeight: '700'
          }}>
            Ready to Transform Your Business?
          </h2>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '1.1rem',
            marginBottom: '2rem',
            lineHeight: '1.8'
          }}>
            Let's discuss how we can help you leverage technology to achieve your business goals.
          </p>
          <Link
            to="/contact"
            style={{
              display: 'inline-block',
              padding: '1.25rem 3rem',
              background: 'var(--gradient-1)',
              borderRadius: '50px',
              color: 'white',
              fontSize: '1.1rem',
              fontWeight: '600',
              textDecoration: 'none',
              transition: 'transform 0.3s ease',
              boxShadow: '0 5px 15px rgba(37, 99, 235, 0.3)'
            }}
          >
            Get in Touch
          </Link>
        </div>
      </section>

      <style>{`
        .blog-content h2 {
          font-size: 2rem;
          color: var(--text-primary);
          margin: 2.5rem 0 1.5rem 0;
          font-weight: 700;
          line-height: 1.3;
        }

        .blog-content h3 {
          font-size: 1.5rem;
          color: var(--text-primary);
          margin: 2rem 0 1rem 0;
          font-weight: 700;
          line-height: 1.3;
        }

        .blog-content h4 {
          font-size: 1.2rem;
          color: var(--text-primary);
          margin: 1.5rem 0 0.75rem 0;
          font-weight: 600;
        }

        .blog-content p {
          margin-bottom: 1.5rem;
          color: var(--text-secondary);
        }

        .blog-content ul, .blog-content ol {
          margin: 1.5rem 0;
          padding-left: 2rem;
        }

        .blog-content li {
          margin-bottom: 0.75rem;
          color: var(--text-secondary);
        }

        .blog-content strong {
          color: var(--text-primary);
          font-weight: 600;
        }

        .blog-content a {
          color: var(--figo-primary);
          text-decoration: underline;
          transition: color 0.3s ease;
        }

        .blog-content a:hover {
          color: var(--figo-secondary);
        }
      `}</style>
    </div>
  );
};

export default BlogPostPage;
